import { Formik } from 'formik';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormGeneratorOnSubmitParams, ReactFormGenerator } from 'react-form-builder';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import { transformFileToBase64 } from '@/modules/common/helpers/files.ts';
import { Button } from 'ui';
import { useIndexedDBState } from 'ui/hooks';
import { INDEXED_DB_STORES } from 'ui/utils';
import {
  RegisterExtendResponse,
  RegisterResult,
  RegisterResultRequest,
  TaskWithRelationsResponse,
} from 'compliance-api';

interface TaskPageBodyProps {
  task: TaskWithRelationsResponse;
  isTaskResolving: boolean;
  registers: RegisterExtendResponse[];
  onResolveTask: (
    formResults: FormGeneratorOnSubmitParams[],
    registers: RegisterResultRequest[],
  ) => Promise<void>;
}

export const TaskPageBody = ({
  task,
  isTaskResolving,
  registers,
  onResolveTask,
}: TaskPageBodyProps) => {
  const auth = useAuth();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const clickHasBeenPerformed = useRef(false);
  const [searchParams] = useSearchParams();
  const association = searchParams.get('association');
  const [formData, setFormData, , isInitialedValue] = useIndexedDBState<
    FormGeneratorOnSubmitParams[]
  >(
    INDEXED_DB_STORES.TASK_FORM_DATA,
    association ? `association-${association}-form-data` : `task-${task.id}-form-data`,
    [],
  );

  useEffect(() => {
    if (!isInitialedValue) return;
    if (!auth.isAuthenticated) return;

    const key = association ? `association-${association}-form-data` : `task-${task.id}-form-data`;

    const savedData = localStorage.getItem(key);
    if (!savedData) return;

    const parsedData = JSON.parse(savedData);

    if (parsedData === true) {
      if (!clickHasBeenPerformed.current) {
        clickHasBeenPerformed.current = true;

        setTimeout(() => {
          submitButtonRef.current?.scrollIntoView({ behavior: 'smooth' });
          submitButtonRef.current?.click();
        }, 500);
      }
    }
  }, [association, auth.isAuthenticated, isInitialedValue, task.id]);

  const formGeneratorRef = useRef<{ validate: () => boolean }>(null);

  const compliantWithRegisters: RegisterResultRequest[] = useMemo(() => {
    const allCompliance = task.form.data.reduce((acc: RegisterResultRequest[], curr) => {
      if (!get(curr, 'compliance', false)) return acc;

      const currData = formData.find(({ id }) => id === get(curr, 'id', 0));

      if (!currData) return acc;

      const complianceResults = get(curr, 'options', [])
        .filter(({ id }) => currData.value.includes(id))
        .map(({ compliance }) => compliance);

      return [
        ...acc,
        ...complianceResults.map(result => ({ register_id: get(curr, 'registerId', 0), result })),
      ];
    }, [] as RegisterResultRequest[]);

    const registers = uniq(allCompliance.map(({ register_id }) => register_id));

    return registers.map(registerId => {
      let result = RegisterResult.Compliant;
      const isRegisterHasInProgressStatus = allCompliance.some(
        ({ register_id: register_idX, result }) =>
          register_idX === registerId && result === RegisterResult.InProgress,
      );
      const isRegisterHasNotCompliantStatus = allCompliance.some(
        ({ register_id: register_idX, result }) =>
          register_idX === registerId && result === RegisterResult.NotCompliant,
      );

      const isRegisterHasNonApplicableStatus = allCompliance.some(
        ({ register_id: register_idX, result }) =>
          register_idX === registerId && result === RegisterResult.NonApplicable,
      );

      if (isRegisterHasInProgressStatus) result = RegisterResult.InProgress;
      if (isRegisterHasNotCompliantStatus) result = RegisterResult.NotCompliant;
      if (isRegisterHasNonApplicableStatus) result = RegisterResult.NonApplicable;

      return { register_id: registerId, result };
    });
  }, [formData, task.form.data]);

  const hasSignatureElement = useMemo(() => {
    const checkForSignature = (items: any[]): boolean => {
      return items.some((item: any) => {
        if (!item || typeof item === 'string') return false;
        if (item?.name === 'Signature') return true;

        // Check nested items in container elements
        if ('childItems' in item && Array.isArray(item?.childItems)) {
          return checkForSignature(item?.childItems);
        }
        return false;
      });
    };
    return checkForSignature(task.form.data);
  }, [task.form.data]);

  const handleResolveTask = useCallback(async () => {
    const isValid = formGeneratorRef?.current?.validate && !formGeneratorRef.current.validate();
    if (!isValid) return;

    const adaptedData = formData.map(async item => {
      if (!(item.value instanceof File)) return item;

      const base64File = await transformFileToBase64(item.value);
      return { ...item, value: base64File, filename: item.value.name, filesize: item.value.size };
    });

    const formResults = await Promise.all(adaptedData);

    await onResolveTask(formResults, compliantWithRegisters);
  }, [compliantWithRegisters, formData, onResolveTask]);

  return (
    <div className="mx-auto border rounded-lg p-6 max-w-full w-[720px]">
      {/*Dummy formik so InlineComponent logic works*/}
      <Formik
        initialValues={{}}
        onSubmit={() => {
          console.log('Submitted');
        }}
      >
        <ReactFormGenerator
          imperativeRef={formGeneratorRef}
          items={task.form.data}
          onChange={setFormData}
          registers={registers}
          initAnswers={formData}
          useInitAnswers
          readOnly
          editing={true}
        />
      </Formik>
      <Button
        ref={submitButtonRef}
        disabled={isTaskResolving}
        loading={isTaskResolving}
        variant="primary"
        onClick={handleResolveTask}
        className="w-auto mt-5 ml-auto"
      >
        {hasSignatureElement ? 'Next' : 'Submit'}
      </Button>
    </div>
  );
};
